import React, { useState, useEffect, useRef } from 'react';
import { useShop } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Helper component for inline date editing and "Click to close"
function ClosedDateCell({ tx, onRefresh }) {
  const [isEditing, setIsEditing] = useState(false);
  const [dateValue, setDateValue] = useState(tx.closed_date || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // PATCH closed_at for the given transaction id
  const patchClosedAt = async (newDate) => {
    try {
      const res = await fetch(`${API_URL}/transactions/${tx.id}/closed-at`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ closed_at: newDate || null })
      });
      if (res.ok) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error updating closed date:', err);
    }
  };

  // ✓ button: save the current date value (or clear if empty)
  const handleConfirm = async () => {
    await patchClosedAt(dateValue || null);
    setIsEditing(false);
  };

  // ✗ button: clear closed_at and unclose the transaction
  const handleCancel = async () => {
    await patchClosedAt(null);
    setIsEditing(false);
  };

  const handleCloseTransaction = async () => {
    try {
      const res = await fetch(`${API_URL}/transactions/${tx.id}/close`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error closing transaction:', err);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          className="border border-indigo-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          onClick={handleConfirm}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 transition-colors flex-shrink-0"
          title="Save"
        >
          ✓
        </button>
        <button
          onClick={handleCancel}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 transition-colors flex-shrink-0"
          title="Clear / Unclose"
        >
          ✗
        </button>
      </div>
    );
  }

  if (tx.closed_date) {
    return (
      <span
        onClick={() => { setDateValue(tx.closed_date || ''); setIsEditing(true); }}
        className="cursor-pointer hover:bg-indigo-50 hover:text-indigo-700 rounded px-1 transition-colors"
        title="Click to edit closed date"
      >
        {tx.closed_date}
      </span>
    );
  }

  return (
    <span
      onClick={handleCloseTransaction}
      className="cursor-pointer text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded px-2 py-0.5 hover:bg-green-200 transition-colors"
      title="Click to close this transaction"
    >
      Click to close
    </span>
  );
}

export default function TaxReports() {
  const { shops, currentShop } = useShop();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Default the shop filter to the active shop (persisted in localStorage),
  // refined below once currentShop finishes loading.
  const [shopId, setShopId] = useState(() => localStorage.getItem('activeShopId') || '');
  const [filterClosed, setFilterClosed] = useState('true');
  const [quickRange, setQuickRange] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const tableScrollRef = useRef(null);
  const [pendingScrollTop, setPendingScrollTop] = useState(null);
  const shopDefaultedRef = useRef(false);

  // Default the shop filter to the active shop once shops have loaded.
  // Runs only once so the user can still switch to "All Shops" afterwards.
  useEffect(() => {
    if (!shopDefaultedRef.current && currentShop) {
      setShopId(currentShop.shop_id);
      shopDefaultedRef.current = true;
    }
  }, [currentShop]);

  // Restore scroll position after transactions update
  useEffect(() => {
    if (pendingScrollTop !== null && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = pendingScrollTop;
      setPendingScrollTop(null);
    }
  }, [transactions, pendingScrollTop]);

  // Refresh wrapper that preserves scroll position
  const refreshWithScroll = () => {
    setPendingScrollTop(tableScrollRef.current?.scrollTop ?? null);
    fetchReport(dateFrom, dateTo, shopId, filterClosed);
  };

  // Scroll table to bottom (totals row)
  const scrollToBottom = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTo({
        top: tableScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Helper: format date as YYYY-MM-DD
  const formatDate = (d) => d.toISOString().split('T')[0];

  // Today's date
  const today = new Date();

  // Quick date range handlers (matching Performance.js)
  // Ranges are half-open: date_from inclusive, date_to exclusive.
  // "This *" presets end at the first day of the NEXT period so the
  // current period (including today) is fully covered.

  // This Week: Sunday of this week → Next Sunday (excl.)
  const setThisWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayNextWeek = new Date(sundayThisWeek);
    sundayNextWeek.setDate(sundayThisWeek.getDate() + 7);
    setDateFrom(formatDate(sundayThisWeek));
    setDateTo(formatDate(sundayNextWeek));
  };

  // Last Week: Sunday of last week → Sunday of this week (excl.)
  const setLastWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayLastWeek = new Date(sundayThisWeek);
    sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
    setDateFrom(formatDate(sundayLastWeek));
    setDateTo(formatDate(sundayThisWeek));
  };

  // This Month: 1st of this month → 1st of next month (excl.)
  const setThisMonth = () => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonthStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    setDateFrom(formatDate(monthStart));
    setDateTo(formatDate(nextMonthStart));
  };

  // Last Month: 1st of last month → 1st of this month (excl.)
  const setLastMonth = () => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setDateFrom(formatDate(lastMonthStart));
    setDateTo(formatDate(thisMonthStart));
  };

  // This Quarter: 1st of this quarter → 1st of next quarter (excl.)
  const setThisQuarter = () => {
    const quarter = Math.floor(today.getMonth() / 3);
    const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
    const nextQuarterStart = new Date(today.getFullYear(), (quarter + 1) * 3, 1);
    setDateFrom(formatDate(quarterStart));
    setDateTo(formatDate(nextQuarterStart));
  };

  // Last Quarter: 1st of last quarter → 1st of this quarter (excl.)
  const setLastQuarter = () => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
    const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
    setDateFrom(formatDate(lastQuarterStart));
    setDateTo(formatDate(thisQuarterStart));
  };

  // This Year: Jan 1 this year → Jan 1 next year (excl.)
  const setThisYear = () => {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    const nextYearStart = new Date(today.getFullYear() + 1, 0, 1);
    setDateFrom(formatDate(yearStart));
    setDateTo(formatDate(nextYearStart));
  };

  // Last Year: Jan 1 last year → Jan 1 this year (excl.)
  const setLastYear = () => {
    const lastYear = today.getFullYear() - 1;
    const lastYearStart = new Date(lastYear, 0, 1);
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(lastYearStart));
    setDateTo(formatDate(thisYearStart));
  };

  // All Time: 2000-01-01 → Tomorrow (excl., so today is included)
  const setAllTime = () => {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDateFrom('2000-01-01');
    setDateTo(formatDate(tomorrow));
  };

  // Quick range dropdown handler
  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    setQuickRange(range);
    const rangeHandlers = {
      'this-week': setThisWeek,
      'last-week': setLastWeek,
      'this-month': setThisMonth,
      'last-month': setLastMonth,
      'this-quarter': setThisQuarter,
      'last-quarter': setLastQuarter,
      'this-year': setThisYear,
      'last-year': setLastYear,
      'all-time': setAllTime,
    };
    if (rangeHandlers[range]) {
      rangeHandlers[range]();
    } else {
      setDateFrom('');
      setDateTo('');
    }
  };

  // Reset filters (shop returns to the active shop, matching the default)
  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setShopId(currentShop ? currentShop.shop_id : '');
    setQuickRange('');
    setFilterClosed('any');
    setHasSearched(false);
    setTransactions([]);
    setTotals(null);
  };

  // Fetch tax report data - single API call returns both rows and computed totals
  const fetchReport = async (from, to, sId, closed) => {
    if (!from || !to) return;

    setLoading(true);
    setHasSearched(true);
    setTotals(null);

    try {
      const params = new URLSearchParams();
      params.set('date_from', from);
      params.set('date_to', to);
      if (sId) params.set('shop_id', sId);
      if (closed !== 'any') params.set('is_closed', closed);

      const txRes = await fetch(`${API_URL}/transactions/tax-report?${params.toString()}`, {
        credentials: 'include'
      });
      if (!txRes.ok) throw new Error('Failed to fetch tax report');
      const data = await txRes.json();

      // Response is { taxRows: [...], totals: {...} }
      setTransactions(data.taxRows || []);
      setTotals(data.totals || null);
    } catch (err) {
      console.error('Error fetching tax report:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch when date range is set via quick buttons
  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchReport(dateFrom, dateTo, shopId, filterClosed);
    }
  }, [dateFrom, dateTo, shopId, filterClosed]);

  // CSV Export
  const exportCSV = () => {
    if (!transactions.length) return;

    const headers = ['TX Date', 'Shop', 'Last Payment', 'Closed Date', 'Customer', 'Subtotal', 'Tax', 'Total', 'Payments', 'Returns'];

    const formatCurrency = (val) => (parseFloat(val) || 0).toFixed(2);

    const rows = transactions.map(tx => [
      tx.tx_date || '',
      tx.shop_id || '',
      tx.last_payment_date || '',
      tx.closed_date || '',
      tx.customer_name,
      formatCurrency(tx.subtotal),
      formatCurrency(tx.tax),
      formatCurrency(tx.total),
      formatCurrency(tx.payments),
      formatCurrency(tx.returns)
    ]);

    // Add totals row
    if (totals) {
      rows.push([
        'Totals', '', '', '', '',
        formatCurrency(totals.subtotal),
        formatCurrency(totals.tax),
        formatCurrency(totals.total),
        formatCurrency(totals.payments),
        formatCurrency(totals.returns)
      ]);
    }

    // Footer header row: repeat column names at the bottom so the
    // columns are identifiable when viewing the end of the file
    rows.push(headers);

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tax-report-${dateFrom}_to_${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // PDF Export (server-generated, landscape US Letter; opens in a new tab)
  const exportPDF = () => {
    if (!transactions.length) return;

    const params = new URLSearchParams();
    params.set('date_from', dateFrom);
    params.set('date_to', dateTo);
    if (shopId) params.set('shop_id', shopId);
    if (filterClosed !== 'any') params.set('is_closed', filterClosed);

    window.open(`${API_URL}/transactions/tax-report/pdf?${params.toString()}`, '_blank');
  };

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return num.toFixed(2);
  };

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Tax Reports</h1>
        {hasSearched && (
          <button onClick={handleReset} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
            Reset Filters
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-xl p-4 mb-6 border">
        <div className="flex flex-wrap items-end gap-4">
        {/* Shop Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Shop:</label>
          <select
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Shops</option>
            {shops.map((shop) => (
              <option key={shop.id} value={shop.shop_id}>
                {shop.shop_name}
              </option>
            ))}
          </select>
        </div>

        {/* Date From */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Date To */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Closed Filter - 3-way segmented button */}
        <div className="flex items-center gap-2 self-end mb-2">
          <span className="text-sm font-medium text-gray-600">Closed:</span>
          <div className="flex rounded-md shadow-sm">
            {['any', 'true', 'false'].map(val => (
              <button
                key={val}
                onClick={() => setFilterClosed(val)}
                className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                  filterClosed === val
                    ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                } ${val !== 'any' ? '-ml-px' : ''}`}
              >
                {val === 'any' ? 'Any' : val === 'true' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Range Dropdown */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Quick Range</label>
          <select
            value={quickRange}
            onChange={handleQuickRangeChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select Range</option>
            <option value="this-week">This Week</option>
            <option value="last-week">Last Week</option>
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="this-quarter">This Quarter</option>
            <option value="last-quarter">Last Quarter</option>
            <option value="this-year">This Year</option>
            <option value="last-year">Last Year</option>
            <option value="all-time">All Time</option>
          </select>
        </div>

        {/* Export CSV Button */}
        <button
          onClick={exportCSV}
          disabled={!transactions.length}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>

        {/* Export PDF Button */}
        <button
          onClick={exportPDF}
          disabled={!transactions.length}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Export PDF
        </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-sm text-gray-500">Loading report...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && hasSearched && transactions.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No transactions found for the selected date range.</p>
        </div>
      )}

      {/* Initial State */}
      {!loading && !hasSearched && (
        <div className="bg-white shadow-sm rounded-xl p-12 text-center border">
          <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Select a date range</h3>
          <p className="mt-1 text-sm text-gray-500">Choose a date range above to view your tax report.</p>
        </div>
      )}

      {/* Data Table */}
      {!loading && transactions.length > 0 && (
        <div ref={tableScrollRef} className="bg-white rounded-lg shadow overflow-x-auto overflow-y-auto max-h-[calc(100vh-320px)]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">TX Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Shop</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Last Payment</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Closed Date</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-700">Customer</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-700">Subtotal</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-700">Tax</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-700">Total</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-700">Payments</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-700">Returns</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => {
                const hasReturns = parseFloat(tx.returns) > 0;
                return (
                  <tr key={tx.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${hasReturns ? 'bg-red-50' : 'bg-green-50'}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{tx.tx_date || ''}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{tx.shop_id || ''}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{tx.last_payment_date || ''}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <ClosedDateCell tx={tx} onRefresh={refreshWithScroll} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{tx.customer_name}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(tx.subtotal)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(tx.tax)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(tx.total)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(tx.payments)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right" style={hasReturns ? { color: 'red' } : {}}>
                      {formatCurrency(tx.returns)}
                    </td>
                  </tr>
                );
              })}
              {/* Totals Row */}
              {totals && (
                <>
                  <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-700">
                    <td className="px-3 py-2">TX Date</td>
                    <td className="px-3 py-2">Shop</td>
                    <td className="px-3 py-2">Last Payment</td>
                    <td className="px-3 py-2">Closed Date</td>
                    <td className="px-3 py-2">Customer</td>
                    <td className="px-3 py-2 text-right">Subtotal</td>
                    <td className="px-3 py-2 text-right">Tax</td>
                    <td className="px-3 py-2 text-right">Total</td>
                    <td className="px-3 py-2 text-right">Payments</td>
                    <td className="px-3 py-2 text-right">Returns</td>
                  </tr>
                  <tr className="border-t-2 border-gray-300 bg-white font-bold">
                    <td className="px-3 py-3">Totals</td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3 text-right">{formatCurrency(totals.subtotal)}</td>
                    <td className="px-3 py-3 text-right">{formatCurrency(totals.tax)}</td>
                    <td className="px-3 py-3 text-right">{formatCurrency(totals.total)}</td>
                    <td className="px-3 py-3 text-right">{formatCurrency(totals.payments)}</td>
                    <td className="px-3 py-3 text-right">{formatCurrency(totals.returns)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating "Scroll to Totals" Button */}
      {!loading && transactions.length > 0 && totals && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-6 right-6 z-50 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-colors w-12 h-12 flex items-center justify-center"
          title="Scroll to totals"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}
    </div>
  );
}
